import { BLOOD_TYPE_LABELS } from "@dentacore/shared-types";
import type { ContractType, HrPerson, SalaryEntry } from "@dentacore/shared-types";

import { formatPhone } from "../ui/countries";

/**
 * Staff → Team (STORY-181c, HR-D1, HR-D3, HR-D8, HR-D13–HR-D15) — what the
 * list and the panel say about a person, worked out from their HR record.
 *
 * **A tone is a shape as well as a colour** (the story's own rule): *bad*
 * (something has lapsed) is drawn as a diamond, *warm* (something wants doing
 * soon) as a circle, and each says its meaning to a screen reader — red against
 * brown is not a difference a colour-blind reader can see.
 */
export type Tone = "bad" | "warm" | "calm";

export interface Reason {
  tone: Tone;
  /** What it is, e.g. "Registration expired". */
  text: string;
  /** The detail under it, e.g. "3 days ago · still bookable". */
  sub: string;
}

export const TONE_WORDS: Record<Tone, string> = {
  bad: "Expired or lapsed",
  warm: "Needs attention",
  calm: "",
};

const TONE_ORDER: Record<Tone, number> = { bad: 0, warm: 1, calm: 2 };

/**
 * Why somebody needs an eye, the most actionable reason first — the prototype's
 * order: a lapsed certificate, then one due, a renewal waiting for a check,
 * leave over the allowance, off sick, and a probation about to end.
 */
export function reasonsFor(person: HrPerson, today: string): Reason[] {
  const reasons: Reason[] = [];

  for (const row of person.attention) {
    const name = row.certificate ?? "";
    if (row.kind === "CERTIFICATE_EXPIRED" && row.date !== null) {
      const ago = daysBetween(row.date, today);
      reasons.push({
        tone: "bad",
        text: `${name} expired`,
        sub: `${days(ago)} ago${row.neededToTreat ? " · still bookable" : ""}`,
      });
    } else if (row.kind === "CERTIFICATE_DUE" && row.date !== null) {
      reasons.push({
        tone: "warm",
        text: `${name} due`,
        sub: `in ${days(daysBetween(today, row.date))}`,
      });
    } else if (row.kind === "RENEWAL_WAITING") {
      reasons.push({ tone: "warm", text: `${name} renewal`, sub: "waiting for a check" });
    }
  }

  const left = person.leave.remainingDays;
  if (left !== null && left < 0) {
    reasons.push({ tone: "warm", text: "Over leave allowance", sub: `by ${days(-left)}` });
  }
  if (person.today === "SICK") reasons.push({ tone: "warm", text: "Off sick", sub: "today" });

  const probation = person.attention.find((row) => row.kind === "PROBATION_ENDING");
  if (probation?.date != null) {
    reasons.push({ tone: "calm", text: "Probation ends", sub: shortDate(probation.date) });
  }

  // Stable within a tone, so the certificate order the server gave survives.
  return reasons
    .map((reason, index) => ({ reason, index }))
    .sort((a, b) => TONE_ORDER[a.reason.tone] - TONE_ORDER[b.reason.tone] || a.index - b.index)
    .map(({ reason }) => reason);
}

/** The worst tone on a person — what their dot says. */
export function toneOf(reasons: readonly Reason[]): Tone {
  return reasons[0]?.tone ?? "calm";
}

/** Who needs an eye first, then by name — the list's order. */
export function byNeed(
  people: readonly HrPerson[],
  today: string,
): { person: HrPerson; reasons: Reason[] }[] {
  return people
    .map((person) => ({ person, reasons: reasonsFor(person, today) }))
    .sort(
      (a, b) =>
        TONE_ORDER[toneOf(a.reasons)] - TONE_ORDER[toneOf(b.reasons)] ||
        a.person.name.localeCompare(b.person.name),
    );
}

/** Search the team: a name, a job, or a location, ignoring case. */
export function matches(person: HrPerson, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === "") return true;
  return [person.name, person.role.replace(/_/g, " "), ...person.locations.map((row) => row.name)]
    .join(" ")
    .toLowerCase()
    .includes(needle);
}

/** The personal rows, in the order `personalFacts` draws them (STORY-192). */
export const PERSONAL_LABELS = ["Born", "Blood type", "Emergency contact"];

/** Whole years from a `YYYY-MM-DD` birth to `YYYY-MM-DD` today. */
export function yearsBetween(born: string, today: string): number {
  const years = Number(today.slice(0, 4)) - Number(born.slice(0, 4));
  return today.slice(5) < born.slice(5) ? years - 1 : years;
}

/**
 * Date of birth, blood type and emergency contact (SP-D1), as the Team panel
 * and Me both draw them. A withheld blood type says so rather than "—", which
 * would read as "none recorded" (SP-D3).
 */
export function personalFacts(person: HrPerson, today: string): [string, string][] {
  const { personal } = person;
  const contact = personal.emergencyContact;
  return [
    [
      "Born",
      personal.dateOfBirth === null
        ? "—"
        : `${shortDate(personal.dateOfBirth)} · ${String(yearsBetween(personal.dateOfBirth, today))}`,
    ],
    [
      "Blood type",
      personal.bloodTypeHidden
        ? "Seen by an administrator only"
        : personal.bloodType === null
          ? "—"
          : BLOOD_TYPE_LABELS[personal.bloodType],
    ],
    [
      "Emergency contact",
      contact === null
        ? "—"
        : [
            contact.relation === null ? contact.name : `${contact.name} (${contact.relation})`,
            contact.phoneE164 === null ? null : formatPhone(contact.phoneE164),
          ]
            .filter((part) => part !== null)
            .join(" · "),
    ],
  ];
}

export const CONTRACT_LABELS: Record<ContractType, string> = {
  FULL_TIME: "Full time",
  PART_TIME: "Part time",
};

/** The salary in force today — the newest that has started (AC-D10). Null when none has. */
export function salaryToday(salaries: readonly SalaryEntry[], today: string): SalaryEntry | null {
  let current: SalaryEntry | null = null;
  for (const row of salaries) if (row.effectiveFrom <= today) current = row;
  return current;
}

/** `1` → `1 day`, `3` → `3 days`. Leave can be half days, so it rounds nothing. */
export function days(count: number): string {
  return `${String(count)} ${count === 1 ? "day" : "days"}`;
}

/** Whole days from one `YYYY-MM-DD` to another. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** `2026-11-20` → `20 Nov 2026`. */
export function shortDate(iso: string): string {
  const at = new Date(`${iso}T00:00:00.000Z`);
  return `${String(at.getUTCDate())} ${MONTHS[at.getUTCMonth()] ?? ""} ${String(at.getUTCFullYear())}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
